import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, MapPin, CalendarDays, ClipboardList, Backpack } from 'lucide-react';
import RegistrationForm from '@/components/RegistrationForm';
import { TRYOUT, ASSETS, CONTACT, AGE_GROUPS } from '@/data/content';
import { fadeUp, stagger, viewportOnce, EASE } from '@/lib/motion';

export default function Register() {
  const [params] = useSearchParams();
  const program = params.get('program') || '';

  useEffect(() => {
    document.title = 'Tryouts & Registration | Abu Zama Soccer Academy — Canton, Michigan';
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="register-page">
      <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-24 overflow-hidden" data-testid="tryouts-hero">
        <div className="absolute inset-0 -z-10">
          <img src={ASSETS.squadBanner} alt="ASA squad" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-night/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-transparent to-night/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
            className="text-brand-gold text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold"
          >
            Tryouts & Player Evaluation
          </motion.p>
          <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.92] text-6xl sm:text-7xl lg:text-8xl text-white" data-testid="tryouts-page-headline">
            <span className="block overflow-hidden pb-1"><motion.span className="block" initial={{ y: '110%' }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>READY FOR THE</motion.span></span>
            <span className="block overflow-hidden pb-1"><motion.span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-goldlight via-brand-gold to-brand-goldlight" initial={{ y: '110%' }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: EASE }}>NEXT LEVEL?</motion.span></span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-base sm:text-lg text-gray-300 leading-relaxed"
          >
            {TRYOUT.text}
          </motion.p>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-night" data-testid="tryouts-details">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <ClipboardList size={26} className="text-brand-gold" /> The Evaluation Process
            </h2>
            <ol className="mt-8 space-y-4" data-testid="tryouts-process-list">
              {TRYOUT.process.map((p, i) => (
                <motion.li key={p.step} variants={fadeUp} custom={i} className="glass-card rounded-sm p-5 flex items-start gap-5">
                  <span className="font-display text-3xl text-stroke-gold leading-none shrink-0">{p.step}</span>
                  <div>
                    <h3 className="font-display text-lg tracking-wide text-white uppercase">{p.title}</h3>
                    <p className="mt-1 text-sm text-gray-400 leading-relaxed">{p.text}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
            <div className="mt-10">
              <h3 className="font-display text-2xl text-white uppercase tracking-wide">Age Groups</h3>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="tryouts-age-groups">
                {AGE_GROUPS.map(g => (
                  <span key={g.group} className="border border-white/15 rounded-full px-4 py-1.5 text-sm text-gray-300">
                    <span className="text-brand-gold font-bold">{g.group}</span> · {g.ages}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce} className="space-y-8">
            <div className="glass-card rounded-sm p-7">
              <h3 className="font-display text-2xl text-white uppercase tracking-wide flex items-center gap-3">
                <Backpack size={22} className="text-brand-gold" /> What Players Should Bring
              </h3>
              <ul className="mt-5 space-y-2.5" data-testid="tryouts-bring-list">
                {TRYOUT.bring.map(b => (
                  <li key={b} className="flex items-center gap-2.5 text-sm text-gray-300">
                    <Check size={15} className="text-brand-gold shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-sm p-7">
              <h3 className="font-display text-2xl text-white uppercase tracking-wide">Training Expectations</h3>
              <ul className="mt-5 space-y-2.5" data-testid="tryouts-expectations-list">
                {TRYOUT.expectations.map(e => (
                  <li key={e} className="flex items-center gap-2.5 text-sm text-gray-300">
                    <Check size={15} className="text-brand-red shrink-0" /> {e}
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-sm p-7">
              <h3 className="font-display text-2xl text-white uppercase tracking-wide flex items-center gap-3">
                <CalendarDays size={22} className="text-brand-gold" /> Available Dates & Location
              </h3>
              <ul className="mt-5 space-y-2.5" data-testid="tryouts-dates-list">
                {TRYOUT.dates.map(d => (
                  <li key={d} className="text-sm text-gray-300">{d}</li>
                ))}
              </ul>
              <div className="mt-5 flex items-center gap-2.5 text-sm text-gray-300">
                <MapPin size={15} className="text-brand-gold shrink-0" /> {CONTACT.location}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-night-surface border-t border-white/5" data-testid="tryouts-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <RegistrationForm
              initialProgram={program}
              formType={program.toLowerCase().includes('evaluation') ? 'evaluation' : 'registration'}
              heading={program ? `REGISTER — ${program.toUpperCase()}` : 'BOOK A PLAYER EVALUATION'}
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
